import { streamAgentDmResponse } from "@/lib/agent-dm-stream-client";
import { triggerCoworkerResponse } from "@/lib/coworker-trigger";
import type { AccessTokenGetter } from "@/lib/auth-token";

/**
 * Phase 7b follow-up #5 — extracted from `use-message-send.ts` so the
 * hook's main `sendMessage` callback fits under the 400-LOC ceiling
 * without an `eslint-disable max-lines` marker.
 *
 * Routes the post-send agent reply to the right path:
 *   - Coworker DM → SSE on `/api/agent/chat`. The server pre-claims
 *     dedup so the LISTEN/NOTIFY scheduler skips this trigger; the
 *     SSE accumulator posts the final message via `postAgentMessage`
 *     exactly once when the stream completes — unread fires on that
 *     single INSERT.
 *   - Channel `@mention` to a single coworker → SSE on `/api/agent/chat`
 *     using a `mention:<channelId>:<coworkerId>` session id, so the
 *     thinking + answer stream live exactly like in DMs.
 *   - Multi-mention message: FIRST coworker streams via SSE, the rest
 *     post via the legacy `/api/coworker/respond` REST fallback (no
 *     live stream, just a final message via the scheduler). Composite
 *     dedup keying on the server (`${trigger}:${coworker}`) keeps the
 *     two paths isolated.
 *   - Channel message with no coworker mention → no agent trigger
 *     (the LISTEN/NOTIFY scheduler still handles non-mention triggers
 *     where applicable).
 *
 * Fire-and-forget — caller doesn't await. All side effects run via
 * `void` to keep the hook's send pipeline non-blocking.
 */
export interface TriggerAgentResponseArgs {
  channelId: string;
  workspaceId: string | null | undefined;
  isCoworkerDm: boolean | undefined;
  coworkerDmUserId: string | null | undefined;
  coworkerIds: Set<string> | undefined;
  mentionIds: string[];
  messageId: string;
  messageContent: string;
  getAccessToken: AccessTokenGetter;
}

export function triggerAgentResponse(args: TriggerAgentResponseArgs): void {
  const {
    channelId,
    workspaceId,
    isCoworkerDm,
    coworkerDmUserId,
    coworkerIds,
    mentionIds,
    messageId,
    messageContent,
    getAccessToken,
  } = args;

  const mentionedCoworkerIds = mentionIds.filter((id) => coworkerIds?.has(id));

  if (isCoworkerDm && coworkerDmUserId && workspaceId) {
    void streamAgentDmResponse({
      channelId,
      workspaceId,
      coworkerId: coworkerDmUserId,
      triggerMessageId: messageId,
      userMessageContent: messageContent,
      getAccessToken,
    });
    return;
  }

  if (mentionedCoworkerIds.length === 0) return;

  // Split the first mentioned coworker (live SSE stream) from any
  // remaining ones (legacy scheduler fallback). Guard on workspaceId —
  // if it's somehow unset we fall back entirely to the scheduler path
  // for ALL mentioned coworkers so the first one still gets a reply
  // instead of silently dropping.
  const [firstCoworkerId, ...restCoworkerIds] = mentionedCoworkerIds;
  if (firstCoworkerId && workspaceId) {
    void streamAgentDmResponse({
      channelId,
      workspaceId,
      coworkerId: firstCoworkerId,
      triggerMessageId: messageId,
      userMessageContent: messageContent,
      getAccessToken,
      sessionPrefix: "mention",
    });
    if (restCoworkerIds.length > 0) {
      void triggerCoworkerResponse({
        getAccessToken,
        channelId,
        messageId,
        content: messageContent,
        mentionIds: restCoworkerIds,
      });
    }
  } else {
    void triggerCoworkerResponse({
      getAccessToken,
      channelId,
      messageId,
      content: messageContent,
      mentionIds: mentionedCoworkerIds,
    });
  }
}
